import { useProgress } from '@react-three/drei';
import React, { useEffect, useState, useMemo } from 'react';
import Smoker from './Smoke';
import { LOADING_SCREEN_DELAY, SVG_ANIMATION_DELAY, INTRO_COLORS } from '../constants';
import Signature from './Signature';

export default function LoadingScreen({ onLoadingComplete, isSoundEnabled, setIsSoundEnabled, signatureColor = INTRO_COLORS.FIRST.TEXT, onColorSchemeChange }) {
    const { progress } = useProgress();
    const [isVisible, setIsVisible] = useState(true);
    const [showLoadingBar, setShowLoadingBar] = useState(true);
    const [showSVG, setShowSVG] = useState(false);
    const [activeScheme, setActiveScheme] = useState('FIRST');
    
    // Мемоизируем компонент дыма
    const smokeComponent = useMemo(() => (
        <div className="smoke-container" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0
        }}>
            <Smoker colorScheme={activeScheme} />
        </div>
    ), [activeScheme]);

    const handleSchemeChange = (scheme) => {
        setActiveScheme(scheme);
        onColorSchemeChange?.(INTRO_COLORS[scheme]);
    };

    const colorSchemes = Object.entries(INTRO_COLORS).map(([key, scheme]) => ({
        key,
        color: scheme.CIRCLE_COLOR,
        stroke: scheme.CIRCLE_STROKE
    }));

    const [loadingBarStyles] = useState({
        width: 200,
        height: 2,
        marginTop: -10,
        background: '#333',
        barColor: '#FFFFFF',
        borderRadius: 2,
        border: {
            width: 1,
            color: '#FFFFFF',
            style: 'solid'
        }
    });

    useEffect(() => {
        if (progress >= 100) {
            setShowLoadingBar(false);
            
            setTimeout(() => {
                setShowSVG(true);
                
                setTimeout(() => {
                    setIsVisible(false);
                    onLoadingComplete?.();
                }, LOADING_SCREEN_DELAY);
            }, SVG_ANIMATION_DELAY);
        }
    }, [progress, onLoadingComplete]);

    if (!isVisible) return null;

    return (
        <div className="loading-screen" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: INTRO_COLORS[activeScheme].BACKGROUND,
            zIndex: 1000,
            color: '#fff',
            fontFamily: 'Arial, sans-serif'
        }}>
            {smokeComponent}
            
            {!isSoundEnabled && showSVG && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 10000
                }}>
                    <button
                        onClick={() => setIsSoundEnabled(true)}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: 'transparent',
                            color: signatureColor,
                            border: `1px solid ${signatureColor}`,
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontFamily: 'HelveticaNeue, Arial, sans-serif',
                            fontSize: '12px',
                            letterSpacing: '0.05em',
                            transition: 'background-color 0.3s ease',
                            backdropFilter: 'blur(5px)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        Enable Sound
                    </button>
                </div>
            )}
            
            <div className="content-container" style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                width: '600px',
                height: '600px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <style>
                    {`
                        @keyframes blink {
                            0% { opacity: 1; }
                            50% { opacity: 0; }
                            100% { opacity: 1; }
                        }
                    `}
                </style>
                <div className="signature-container" style={{ 
                    width: '100%', 
                    height: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    top: '-20px',
                    transform: 'translateX(15px)'
                }}>
                    <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        transform: 'scale(0.7)'
                    }}>
                        {showSVG && <Signature isSoundEnabled={isSoundEnabled} color={signatureColor} />}
                    </div>
                </div>

                {/* Skip button */}
                {showSVG && (
                    <button
                        onClick={() => {
                            setIsVisible(false);
                            onLoadingComplete?.();
                        }}
                        style={{
                            position: 'absolute',
                            left: '50%',
                            bottom: '160px',
                            transform: 'translateX(-50%)',
                            padding: '8px 24px',
                            backgroundColor: 'transparent',
                            color: signatureColor,
                            border: `1px solid ${signatureColor}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontFamily: 'HelveticaNeue, Arial, sans-serif',
                            fontSize: '14px',
                            letterSpacing: '0.05em',
                            transition: 'all 0.3s ease',
                            backdropFilter: 'blur(5px)',
                            zIndex: 2,
                            animation: 'blink 2s infinite ease-in-out'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        skip
                    </button>
                )}

                {/* Color scheme circles */}
                {showSVG && (
                    <div style={{
                        display: 'flex',
                        gap: '20px',
                        marginTop: '-20px'
                    }}>
                        {colorSchemes.map(({ key, color, stroke }) => (
                            <button
                                key={key}
                                onClick={() => handleSchemeChange(key)}
                                style={{
                                    width: `${INTRO_COLORS[key].CIRCLE_SIZE}px`,
                                    height: `${INTRO_COLORS[key].CIRCLE_SIZE}px`,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    border: activeScheme === key ? `2px solid ${stroke}` : 'none',
                                    outline: activeScheme === key ? `2px solid rgba(255, 255, 255, 0.3)` : 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    transition: 'transform 0.2s ease',
                                    transform: activeScheme === key ? 'scale(1.1)' : 'scale(1)'
                                }}
                            />
                        ))}
                    </div>
                )}

                {showLoadingBar && (
                    <div className="loading-container" style={{
                        position: 'absolute',
                        bottom: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0'
                    }}>
                        <div className="loading-bar-wrapper" style={{
                            padding: '15px 25px',
                            borderRadius: '10px'
                        }}>
                            <div className="loading-bar" style={{
                                width: `${loadingBarStyles.width}px`,
                                height: `${loadingBarStyles.height}px`,
                                background: loadingBarStyles.background,
                                borderRadius: `${loadingBarStyles.borderRadius}px`,
                                overflow: 'hidden',
                                padding: '2px'
                            }}>
                                <div className="loading-bar-progress" style={{
                                    width: `${progress}%`,
                                    height: '100%',
                                    background: signatureColor,
                                    transition: 'width 0.3s ease-in-out'
                                }} />
                            </div>
                        </div>
                        <div className="loading-text" style={{
                            position: 'relative',
                            top: '-10px',
                            padding: '8px 15px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            opacity: 0.8,
                            color: signatureColor
                        }}>
                            Loading... {Math.round(progress)}%
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
} 